import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MDialog from "components/Shared/MDialog.tsx";
import { MSelect, MSpinner } from "components/Shared";
import Button from "@mui/material/Button";
import { getAllActiveUnderwritersForUserRedux, assignCasesToUnderwriter } from "requests/underwriter";
import { updateQuickListInPlace } from "actions/homeowner/quickList";
import responseHandler from "responseHandler";
import { activeUnderwritersSelector } from "selectors/menuItems";

const createQuicklistUpdate = (quickListId, controlNumbers, underwriterCode) => {
  const update = [];
  controlNumbers.forEach(c => {
    update.push({
      controlIDNumber: c,
      underwriterCode
    });
  });
  return {
    id: quickListId,
    key: "controlIDNumber",
    update
  };
};

const UnderwriterAssign = ({
  visible,
  hideDialog,
  controlNumbers,
  makeToast,
  activeUnderwriters,
  fetchingActiveUnderwriters,
  getUnderwriters,
  updateQuickList,
  selectedQuickListId
}) => {
  const [selectedUnderwriter, setSelectedUnderwriter] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    getUnderwriters();
    return (() => { mounted.current = false; });
  }, []);

  useEffect(() => {
    if (activeUnderwriters.length > 0) {
      setSelectedUnderwriter(activeUnderwriters[0].value);
    }
  }, [activeUnderwriters]);

  const assignCallback = () => {
    const update = createQuicklistUpdate(selectedQuickListId, controlNumbers, selectedUnderwriter);
    updateQuickList(update);
    makeToast("Policies assigned.", "success");
    finishAssigning();
  };

  const finishAssigning = () => {
    hideDialog();
    setIsAssigning(false);
  };

  const onSelectionChange = (option) => {
    setSelectedUnderwriter(option.value);
  };

  const onAssign = () => {
    setIsAssigning(true);
    const payload = {
      code: selectedUnderwriter,
      controlIdNumbers: controlNumbers
    };
    assignCasesToUnderwriter(payload, assignCallback, finishAssigning, mounted);
  };

  const getContentActions = () => {
    if (isAssigning) return [];
    const actions = [
      <Button key="cancelAssignUnderwriter" onClick={hideDialog} >Cancel</Button>
    ];
    if (controlNumbers.length > 0) {
      actions.unshift(<Button key="assignUnderwriter" onClick={onAssign} >Assign</Button>);
    }
    return actions;
  };

  return (
    <MDialog
      id="assign-underwriter-dialog"
      className="assign-underwriter-dialog"
      title="Assign to Underwriter"
      contentClassName="assign-underwriter-dialog-content"
      visible={visible}
      contentActions={getContentActions()}
    >
      {(isAssigning || fetchingActiveUnderwriters) && <MSpinner />}
      {controlNumbers.length === 0 && !isAssigning && <p>No policies selected. Please use the checkboxes to select policies to assign.</p>}
      {controlNumbers.length > 0 && <>
        <p>{`${controlNumbers && controlNumbers.length} ${controlNumbers.length > 1 ? "policies" : "policy"} selected. Choose a user to assign to:`}</p>
        <MSelect
          id="underwriter-assign-mselect"
          className="underwriter-assign-dropdown"
          isClearable={false}
          onChange={onSelectionChange}
          options={activeUnderwriters}
          defaultValue={activeUnderwriters[0]}
          getMenuPortalTarget={() => document.querySelector(".assign-underwriter-dialog")}
        />
      </>}
    </MDialog>
  );
};

UnderwriterAssign.propTypes = {
  visible: PropTypes.bool.isRequired, // Show/hide the dialog
  hideDialog: PropTypes.func.isRequired, // Function to be called to set visibility to false
  controlNumbers: PropTypes.arrayOf(PropTypes.number).isRequired,  // Array of control numbers to be assigned
  selectedQuickListId: PropTypes.number,  // Id of the selected quick, used to update data
  // From state
  activeUnderwriters: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchingActiveUnderwriters: PropTypes.bool,
  // From dispatch
  makeToast: PropTypes.func.isRequired,
  getUnderwriters: PropTypes.func.isRequired,
  updateQuickList: PropTypes.func.isRequired,
};

UnderwriterAssign.defaultProps = {
  controlNumbers: [],
  visible: false,
};

const mapStateToProps = state => {
  return {
    activeUnderwriters: activeUnderwritersSelector(state),
    fetchingActiveUnderwriters: state.customer.fetchingActiveUnderwriters,
  };
};

const mapDispatchToProps = dispatch => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
  getUnderwriters() {
    dispatch(getAllActiveUnderwritersForUserRedux());
  },
  updateQuickList(update) {
    dispatch(updateQuickListInPlace(update));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(UnderwriterAssign);